import React, { useEffect, useMemo, useState } from "react";
import { MdCancel, MdCheckCircle, MdSearch, MdSchedule } from "react-icons/md";
import { TfiTicket } from "react-icons/tfi";
import TicketDetails from "./TicketDetails";

import { useAuth } from "../context/AuthContext";
import { getTickets } from "../services/tickets";
import { styles } from "../styles/TicketList.styles";


const getTicketId = (ticket) => ticket.STAMP || ticket.PUBLIC_ID || ticket.id;

const getStatus = (ticket) => {
  const status = String(ticket.ESTADO || ticket.STATUS || ticket.status || "aberto").trim().toLowerCase();
  return status === "pedente" ? "pendente" : status;
};

const getStatusIcon = (status) => {
  if (status === "concluido" || status === "fechado") return MdCheckCircle;
  if (status === "cancelado") return MdCancel;
  return MdSchedule;
};

const getStatusLabel = (status) => ({
  aberto: "Aberto",
  "em andamento": "Em andamento",
  pendente: "Pendente",
  concluido: "Concluído",
  rejeitado: "Rejeitado",
  fechado: "Fechado",
  cancelado: "Cancelado",
}[status] || status);

function getDate(ticket) {
  return ticket.DATA_ATUALIZACAO || ticket.updatedAt || ticket.CRIADO_EM || ticket.createdAt;
}

function TicketList({ isMobile }) {
  const { token } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("todos");
  const [sortOrder, setSortOrder] = useState("recentes");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedTicket, setSelectedTicket] = useState(null);

  useEffect(() => {
    if (!token) return undefined;
    let active = true;

    const loadTickets = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await getTickets();
        const result = Array.isArray(data) ? data : data?.tickets || data?.data || [];
        if (active) setTickets(result);
      } catch (requestError) {
        if (active) setError("Não foi possível carregar os tickets.");
        console.error("Erro ao buscar tickets:", requestError);
      } finally {
        if (active) setLoading(false);
      }
    };

    const loadTimer = setTimeout(loadTickets, 300);
    return () => {
      active = false;
      clearTimeout(loadTimer);
    };
  }, [token]);

  const visibleTickets = useMemo(() => {
    const term = search.trim().toLowerCase();
    return tickets
      .filter((ticket) => {
        const ticketStatus = getStatus(ticket);
        const text = [ticket.TITULO, ticket.title, ticket.DESCRICAO, ticket.description, ticket.NOME, ticket.name, getTicketId(ticket)].join(" ").toLowerCase();
        return (!term || text.includes(term)) && (status === "todos" || ticketStatus === status);
      })
      .sort((first, second) => {
        const firstDate = new Date(getDate(first) || 0).getTime();
        const secondDate = new Date(getDate(second) || 0).getTime();
        return sortOrder === "recentes" ? secondDate - firstDate : firstDate - secondDate;
      });
  }, [tickets, search, status, sortOrder]);

  const formatDate = (value) => {
    if (!value) return "Sem data";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Sem data";

    return new Intl.DateTimeFormat("pt-PT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date).replaceAll("/", "-");
  };

  if (selectedTicket) {
    return (
      <TicketDetails
        ticket={selectedTicket}
        isMobile={isMobile}
        onBack={() => setSelectedTicket(null)}
        onUpdated={async (updatedTicket) => {
          setTickets((currentTickets) => currentTickets.map((ticket) => (
            getTicketId(ticket) === getTicketId(selectedTicket)
              ? { ...ticket, ...updatedTicket }
              : ticket
          )));
          setSelectedTicket(updatedTicket);

          const result = await getTickets();
          if (result?.error) return;

          const refreshedTickets = Array.isArray(result)
            ? result
            : result?.tickets || result?.data || [];
          const refreshedTicket = refreshedTickets.find(
            (ticket) => getTicketId(ticket) === getTicketId(updatedTicket)
          );

          if (refreshedTicket) {
            const mergedTicket = { ...updatedTicket, ...refreshedTicket };
            setTickets(refreshedTickets.map((ticket) => (
              getTicketId(ticket) === getTicketId(mergedTicket) ? mergedTicket : ticket
            )));
            setSelectedTicket(mergedTicket);
          }
        }}
      />
    );
  }

  return (
    <main style={styles.page(isMobile)}>
      <header style={styles.header(isMobile)}>
        <div style={styles.icon(isMobile)}><TfiTicket size={isMobile ? 22 : 26} /></div>
        <div><h1 style={styles.title}>Tickets</h1><p style={styles.subtitle}>Acompanhe e organize os pedidos de suporte.</p></div>
      </header>

      <section style={styles.card(isMobile)}>
        {loading && (
          <div style={styles.loadingOverlay(isMobile)} aria-label="A carregar tickets" role="status">
            <div style={styles.spinner(isMobile)} />
          </div>
        )}
        <div style={styles.toolbar(isMobile)}>
          <div style={{ position: "relative", flex: 1 }}>
            <MdSearch size={19} color="#7a8ba8" style={{ position: "absolute", left: 12, top: 12 }} />
            <input aria-label="Pesquisar tickets" placeholder="Pesquisar tickets..." value={search} onChange={(event) => setSearch(event.target.value)} style={{ ...styles.input, width: "100%", paddingLeft: 38, boxSizing: "border-box" }} />
          </div>
          <select aria-label="Filtrar por estado" value={status} onChange={(event) => setStatus(event.target.value)} style={styles.select}>
            <option value="todos">Todos os estados</option><option value="aberto">Abertos</option><option value="em andamento">Em andamento</option><option value="pendente">Pendente</option><option value="fechado">Fechados</option>
          </select>
          <select aria-label="Ordenar tickets" value={sortOrder} onChange={(event) => setSortOrder(event.target.value)} style={styles.select}>
            <option value="recentes">Mais recentes</option><option value="antigos">Mais antigos</option>
          </select>
        </div>

        {!loading && error && <div role="alert" style={styles.state}>{error}</div>}
        {!loading && !error && visibleTickets.length === 0 && (
          <div style={styles.emptyState(isMobile)}>
            <TfiTicket size={48} style={{ color: "#cbd5e1" }} />
            <p style={styles.emptyMessage}>Nenhum Ticket encontrado</p>
          </div>
        )}
        {!loading && !error && visibleTickets.length > 0 && <div style={styles.list}>
          {visibleTickets.map((ticket) => {
            const ticketStatus = getStatus(ticket);
            const StatusIcon = getStatusIcon(ticketStatus);
            return <article
              key={getTicketId(ticket)}
              style={styles.item}
              role="button"
              tabIndex={0}
              onClick={() => setSelectedTicket(ticket)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  setSelectedTicket(ticket);
                }
              }}
            >
              <div><h2 style={styles.itemTitle}><TfiTicket size={18} aria-hidden="true" />{ticket.TITULO || ticket.title || ticket.NOME || ticket.name || `Ticket ${getTicketId(ticket)}`}</h2><p style={styles.description}>{ticket.DESCRICAO || ticket.description || "Sem descrição disponível."}</p></div>
              <div style={styles.meta}><span style={styles.badge(ticketStatus)}><StatusIcon size={13} aria-hidden="true" />{getStatusLabel(ticketStatus)}</span><time style={styles.date}>{formatDate(getDate(ticket))}</time></div>
            </article>;
          })}
        </div>}
      </section>
    </main>
  );
}

export default TicketList;